/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Table,
  TableContent,
  TableHeader,
  TableHeaderData,
  Input,
  SearchIcon,
  CloseIcon
} from "@forge/common";
import { useTranslation } from "react-i18next";
import AssignSites from "../../../../../AssignSites";
import { ISiteListProps } from "../../../../../../models";
import { GatewaySiteProps } from "../../../../../../models/devicelist";

import "./index.scss";
import { LOCALE_CONST } from "../../../../../../utils/constants";

interface CustomizedTableProps {
  title?: string;
  tableHeaderData: TableHeaderData[];
  tableContentData: any;
  searchOnChange?: any;
  searchValue?: string;
  tableData?: ISiteListProps[];
  onSearchClear?: any
  isEdit?: boolean;
  isDisabled?: boolean;
  selectedData?: GatewaySiteProps[];
  setSiteValueSelection?: (lists: GatewaySiteProps[]) => void;
  isSitesOrgAvailable?: boolean;
  sitesAlreadyAssoData?: ISiteListProps;
}

const CustomizedTable: React.FC<CustomizedTableProps> = ({
  tableHeaderData,
  tableContentData,
  searchOnChange,
  searchValue,
  tableData,
  onSearchClear,
  isEdit,
  title,
  isDisabled,
  selectedData,
  setSiteValueSelection,
  isSitesOrgAvailable,
  sitesAlreadyAssoData
}) => {
  const { t } = useTranslation();

  const [showAssignSite, setShowAssignSite] = useState<boolean>(false);

  const handleConfirm = (lists: GatewaySiteProps[]) => setSiteValueSelection(lists);

  return (
    <>
      <Card className="overviewtable-skip">
        <div className="flex justify-end w-full mb-2 customize-table-wrap">
          {title && <div className="table-custom-title-cls">{title}</div>}
          {((tableData && tableData.length > 0) || searchValue !== "") && (
            <>            
              <div className="w-1/6 mr-4">
                <Input
                  type="text"
                  data-testid="search-test"
                  placeholder={t(LOCALE_CONST.searchByName)}
                  id="search"
                  onChange={searchOnChange}
                  maxLength={50}
                  value={searchValue}
                  prefix={<SearchIcon strokeWidth={2} />}
                  suffix={
                    searchValue ? (
                      <div
                        className="cursor-pointer"
                        data-testid="search-close-test"
                        onClick={onSearchClear}
                      >
                        <CloseIcon data-testid="search-close" />
                      </div>
                    ) : (
                      <></>
                    )
                  }
                  size="regular"
                />
              </div>
              {isEdit &&(
                <Button
                  variant="primary"
                  onClick={() => setShowAssignSite(true)}
                  disabled={isDisabled}
                >
                  {t(LOCALE_CONST.associateSite)}
                </Button>
              )}
            </>
          )}
        </div>

        {tableData && tableData.length > 0 ? (
          <>
            <CardContent x="none" top="none">
              <Table onCard>
                <TableHeader data={tableHeaderData} />
                <TableContent data={tableContentData} />
              </Table>
            </CardContent>
            <CardContent></CardContent>
          </>
        ) : (
          <CardContent className="place-items-center grid">
            {/* <p>{searchValue !== '' ? 'No results found. Please try another search.' : 'No sites selected. Please associate sites by clicking on "Add"'}</p> */}
            {searchValue !== "" ? (
              <p>{t(LOCALE_CONST.noResultsFound)}</p>
            ) : (
              <>
                <p className="text-3xl">{!isDisabled ? t(LOCALE_CONST.noSitesSelected) : t(LOCALE_CONST.orgNotFound)}</p>
                <p className="pt-1 pb-5">
                  {!isDisabled ? t(LOCALE_CONST.associateSiteOnClick) : t(LOCALE_CONST.orgOnboardingStatus)}
                </p>
                {!isDisabled && 
                  <Button
                    variant="primary"
                    onClick={() => setShowAssignSite(true)}
                    disabled={isDisabled}
                    size="large"
                  >
                    {t(LOCALE_CONST.associateSite)}
                  </Button>
                }
              </>
            )}
          </CardContent>
        )}
      </Card>

      <AssignSites
        showAssignSite={showAssignSite}
        onClose={() => setShowAssignSite(false)}
        onConfirm={handleConfirm}
        sitesData={selectedData || []}
        selectedData={tableData || []}
        isSitesOrgAvailable={isSitesOrgAvailable}
        sitesAlreadyAssoData={sitesAlreadyAssoData}
      />
    </>
  );
};

export default CustomizedTable;
